import { useState } from "react";
import { Button, Input, Textarea, Label } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@altimateai/lego";
import { Criterion, CreateCriterionRequest } from "../../services/evals";
import { useInternalContext } from "../../context/InternalContext";

interface CreateCriterionModalProps {
  criterion?: Criterion | null;
  onClose: () => void;
  onSubmit: (data: CreateCriterionRequest) => void;
}

export const CreateCriterionModal: React.FC<CreateCriterionModalProps> = ({
  criterion,
  onClose,
  onSubmit,
}) => {
  const isEditing = !!criterion;

  const { currentTenant } = useInternalContext();
  const [formData, setFormData] = useState({
    name: criterion?.name || "",
    description: criterion?.description || "",
    tenant: criterion?.tenant || "All",
  });

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="al-max-w-screen-xl al-max-h-[90vh] al-overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Criterion" : "Create Criterion"}</DialogTitle>
        </DialogHeader>

        <div className="al-space-y-4">
          <div>
            <Label htmlFor="criterion-name" className="al-text-sm al-font-semibold al-mb-1">
              Name
            </Label>
            <Input
              id="criterion-name"
              type="text"
              className="al-mt-1"
              placeholder="e.g., Accuracy"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="criterion-description" className="al-text-sm al-font-semibold al-mb-1">
              Description
            </Label>
            <Textarea
              id="criterion-description"
              className="al-h-24 al-mt-1"
              placeholder="Describe what this criterion evaluates..."
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div>
            <div className="al-text-sm al-font-semibold al-mb-2">Tenant Visibility</div>
            <div className="al-space-y-2">
              <label htmlFor="tenant-all" className="al-cursor-pointer">
                <Stack direction="row" align="center" gap="lg">
                  <input
                    id="tenant-all"
                    type="radio"
                    name="test-case-tenant-visibility"
                    checked={formData.tenant === "All"}
                    onChange={() => setFormData({ ...formData, tenant: "All" })}
                    className="al-w-4 al-h-4 al-text-blue-600 focus:al-ring-blue-500"
                  />
                  <span className="al-text-sm al-text-gray-700 al-ml-3">All Tenants</span>
                </Stack>
              </label>
              {currentTenant && (
                <label htmlFor="tenant-current" className="al-cursor-pointer">
                  <Stack direction="row" align="center" gap="lg">
                    <input
                      id="tenant-current"
                      type="radio"
                      name="test-case-tenant-visibility"
                      checked={formData.tenant !== "All"}
                      onChange={() => setFormData({ ...formData, tenant: currentTenant })}
                      className="al-w-4 al-h-4 al-text-blue-600 focus:al-ring-blue-500"
                    />
                    <span className="al-text-sm al-text-gray-700 al-ml-3">
                      {currentTenant} Only
                    </span>
                  </Stack>
                </label>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="al-mt-4">
          <Button onClick={onClose} variant="outline" size="sm">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit}>{isEditing ? "Update" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
